import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Plus, Edit, Trash2, Eye, Languages, Upload, X } from 'lucide-react';

const AdminNews = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title_de: '',
    title_en: '',
    content_de: '',
    content_en: '',
    excerpt_de: '',
    excerpt_en: '',
    image_url: '',
    category: 'news',
    published: true
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news?published=true');
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleAutoTranslate = async () => {
    // Check if German fields are filled
    if (!formData.title_de && !formData.content_de && !formData.excerpt_de) {
      alert('Bitte füllen Sie zuerst die deutschen Felder aus!');
      return;
    }

    setTranslating(true);
    try {
      const response = await api.post('/news/translate', {
        title_de: formData.title_de,
        content_de: formData.content_de,
        excerpt_de: formData.excerpt_de
      });

      // Update form with translations
      setFormData({
        ...formData,
        title_en: response.data.title_en || formData.title_en,
        content_en: response.data.content_en || formData.content_en,
        excerpt_en: response.data.excerpt_en || formData.excerpt_en
      });

      alert('✅ Übersetzung erfolgreich! Sie können die Texte jetzt anpassen.');
    } catch (error) {
      console.error('Translation error:', error);
      if (error.response?.status === 503) {
        alert('⚠️ DeepL API nicht konfiguriert. Bitte DEEPL_API_KEY in der .env Datei setzen.');
      } else {
        alert('❌ Übersetzung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setTranslating(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Bitte wählen Sie eine Bilddatei aus!');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Bild ist zu groß! Maximale Größe: 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/news/upload-image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update form data with uploaded image URL
      setFormData({
        ...formData,
        image_url: response.data.image_url
      });

      // Set preview
      setImagePreview(response.data.image_url);
      
      alert('✅ Bild erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('❌ Fehler beim Hochladen des Bildes');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      image_url: ''
    });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await api.put(`/news/${editingNews.id}`, formData);
      } else {
        await api.post('/news', formData);
      }
      setShowModal(false);
      setEditingNews(null);
      setImagePreview(null);
      setFormData({
        title_de: '',
        title_en: '',
        content_de: '',
        content_en: '',
        excerpt_de: '',
        excerpt_en: '',
        image_url: '',
        category: 'news',
        published: true
      });
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Error saving news');
    }
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({
      title_de: item.title_de,
      title_en: item.title_en,
      content_de: item.content_de,
      content_en: item.content_en,
      excerpt_de: item.excerpt_de || '',
      excerpt_en: item.excerpt_en || '',
      image_url: item.image_url || '',
      category: item.category,
      published: item.published
    });
    // Set preview if image exists
    if (item.image_url) {
      setImagePreview(item.image_url);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this news?')) {
      try {
        await api.delete(`/news/${id}`);
        fetchNews();
      } catch (error) {
        console.error('Error deleting news:', error);
        alert('Error deleting news');
      }
    }
  };

  return (
    <div data-testid="admin-news-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('admin.news_management')}</h1>
        <button
          onClick={() => {
            setEditingNews(null);
            setImagePreview(null);
            setFormData({
              title_de: '',
              title_en: '',
              content_de: '',
              content_en: '',
              excerpt_de: '',
              excerpt_en: '',
              image_url: '',
              category: 'news',
              published: true
            });
            setShowModal(true);
          }}
          data-testid="create-news-button"
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>{t('common.create')}</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title (DE)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Published</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {news.map((item) => (
                <tr key={item.id} data-testid={`news-row-${item.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{item.title_de}</div>
                    <div className="text-sm text-gray-500">{item.title_en}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${item.published ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.published ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        data-testid={`edit-news-${item.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        data-testid={`delete-news-${item.id}`}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-4">{editingNews ? t('common.edit') : t('common.create')} News</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title (DE)</label>
                  <input
                    type="text"
                    name="title_de"
                    value={formData.title_de}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Title (EN)</label>
                  <input
                    type="text"
                    name="title_en"
                    value={formData.title_en}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Excerpt (DE)</label>
                  <textarea
                    name="excerpt_de"
                    value={formData.excerpt_de}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Excerpt (EN)</label>
                  <textarea
                    name="excerpt_en"
                    value={formData.excerpt_en}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Auto-translate button */}
              <div className="flex justify-center py-4">
                <button
                  type="button"
                  onClick={handleAutoTranslate}
                  disabled={translating}
                  className="flex items-center space-x-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Languages className="w-5 h-5" />
                  <span>{translating ? 'Übersetze...' : 'DE → EN Übersetzen'}</span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Content (DE)</label>
                  <textarea
                    name="content_de"
                    value={formData.content_de}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Content (EN)</label>
                  <textarea
                    name="content_en"
                    value={formData.content_en}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium mb-2">Bild</label>
                <div className="space-y-3">
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="relative inline-block">
                      <img 
                        src={imagePreview.startsWith('http') ? imagePreview : `${process.env.REACT_APP_BACKEND_URL}${imagePreview}`}
                        alt="Preview" 
                        className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 shadow-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  {!imagePreview && (
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer">
                        <Upload className="w-5 h-5" />
                        <span>{uploadingImage ? 'Hochladen...' : 'Bild hochladen'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                      <span className="text-sm text-gray-500">oder</span>
                      <input
                        type="url"
                        name="image_url"
                        value={formData.image_url}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value) {
                            setImagePreview(e.target.value);
                          }
                        }}
                        placeholder="Bild-URL eingeben"
                        className="flex-1 px-3 py-2 border rounded-lg"
                      />
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    Laden Sie ein Bild hoch (max. 5MB) oder geben Sie eine externe Bild-URL ein
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="news">News</option>
                    <option value="events">Events</option>
                    <option value="updates">Updates</option>
                  </select>
                </div>
                <div></div>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="published"
                    checked={formData.published}
                    onChange={handleChange}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Published</span>
                </label>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingNews(null);
                    setImagePreview(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
